'use client';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import cn from '@/lib/clsxm';
import handleGA4Event, { eventTracker } from '@/lib/service-workers';

import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import XImage from '@/components/@base/x-image';
import Auth from '@/components/app/auth/components';
import { SOCIAL_MEDIA_TYPE } from '@/components/app/product/components/@shared/type';
import Props from '@/components/app/product/components/tabs/share-buttons/type';
import {
  getSingleProductOptions,
  useGetAddOnsInfo,
  usePostFavorites,
} from '@/components/app/product/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import useCommonModalStore from '@/store/common-modal-store';

import {
  DATA_CY_DISLIKE_BTN,
  DATA_CY_FAVORITE_BTN,
  DATA_CY_LIKE_BTN,
  DATA_CY_LINKEDIN_ICON,
  DATA_CY_SHARE_BTN,
  DATA_CY_TELEGRAM_ICON,
  DATA_CY_TWITTER_ICON,
  DATA_CY_WHATSAPP_ICON,
} from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';
import { SITE_URL } from '@/constant/routes';

const { product: productLocalization } = pageLevelLocalization;

export default function ShareButtons({ slug, productId }: Props) {
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [expandShareIcons, setExpandShareIcons] = useState<boolean>(false);
  const { data, isPending: isAddonsDataPending } = useGetAddOnsInfo({ slug });
  const { mutate: postFavorites } = usePostFavorites();
  const { profile } = useGetProfile();
  const { data: product } = useSuspenseQuery(getSingleProductOptions(slug));
  const { setShow } = useCommonModalStore();
  useEffect(() => {
    data && setIsFavorite(data.hasFavorite);
  }, [data, profile]);

  const handleLikeClick = () => {
    if (profile) {
      postFavorites(
        {
          product_id: productId,
        },
        {
          onSuccess: () => {
            let addToWishListData = {
              item_id: product.id,
              item_name: product.title,
              price: product.price,
              affiliation: product?.store?.slug,
              index: 0,
              quantity: 1,
              discount: product.discount,
            };

            product.categories?.map((item, index) => {
              addToWishListData[`item_category${index !== 0 ? index + 1 : ''}`] = item.title;
            });
            eventTracker(profile?.mobile, 'purchase_package_list', new Date().toISOString(), {
              product_id: product?.id,
              product_name: product?.title,
              category: product?.categories?.map((item) => ({
                $oid: item?.id,
              })),
            });
            handleGA4Event('add_to_wishlist', [addToWishListData], {
              currency: 'USD',
              value: product?.price,
            });
          },
        },
      );

      setIsFavorite(!isFavorite);
      showToast({
        message: isFavorite
          ? productLocalization.unlikeSuccessMessage
          : productLocalization.likeSuccessMessage,
        type: 'success',
      });
    } else {
      handleAuthModal();
    }
  };

  const handleAuthModal = () => {
    setShow(true, {
      DialogPanelProps: {
        className: 'rounded-xl overflow-hidden shadow-none!  p-0! bg-transparent! ',
      },
      Body() {
        return <Auth />;
      },
    });
  };

  const handleShareClick = () => {
    setExpandShareIcons((prev) => !prev);
  };

  const generateShareUrl = (type: SOCIAL_MEDIA_TYPE): string => {
    const productUrl = `${SITE_URL}/web/${slug}`;
    const productTitle = product.metaTitle || product.title;

    switch (type) {
      case SOCIAL_MEDIA_TYPE.LINKEDIN:
        return `https://www.linkedin.com/sharing/share-offsite/?url=${productUrl}`;
      case SOCIAL_MEDIA_TYPE.WHATSAPP:
        return `https://api.whatsapp.com/send?text=${productTitle}&${productUrl}`;
      case SOCIAL_MEDIA_TYPE.TWITTER:
        return `https://twitter.com/intent/tweet?url=${productUrl}&text=${productTitle}`;
      case SOCIAL_MEDIA_TYPE.TELEGRAM:
        return `https://t.me/share/url?url=${productUrl}&text=${productTitle}`;
      default:
        return '';
    }
  };

  return (
    <Container center className='absolute top-0 bottom-0 left-0 hidden gap-2 md:flex'>
      {isAddonsDataPending ? (
        <Container className='-mt-1'>
          <Skeleton width={46} height={46} />
        </Container>
      ) : (
        <Container
          className={cn(
            'h-12 w-12 cursor-pointer rounded-lg bg-[#F9FAFC] p-3.5 hover:bg-[#E5E8EB]',
            expandShareIcons && 'hidden',
          )}
          onClick={handleLikeClick}
          data-cy={DATA_CY_FAVORITE_BTN}
        >
          {isFavorite ? (
            <XImage
              src={imagesList.icons.dislikeIcon}
              alt={productLocalization.dislike}
              width={20}
              height={20}
              data-cy={DATA_CY_LIKE_BTN}
            />
          ) : (
            <XImage
              src={imagesList.icons.likeIcon}
              alt={productLocalization.like}
              width={20}
              height={20}
              data-cy={DATA_CY_DISLIKE_BTN}
            />
          )}
        </Container>
      )}

      <Container
        center
        className={cn(
          'network-list flex-row-reverse gap-4',
          expandShareIcons && 'open z-20 bg-white',
        )}
      >
        <Container
          className={cn(
            'network-item h-12 w-12 cursor-pointer rounded-lg bg-[#5777AC0A] p-3.5',
          )}
          data-cy={DATA_CY_LINKEDIN_ICON}
        >
          <a
            href={generateShareUrl(SOCIAL_MEDIA_TYPE.LINKEDIN)}
            target='_blank'
            rel='noreferrer'
          >
            <XImage
              src={imagesList.icons.linkedinIcon}
              alt='LinkedIn'
              width={20}
              height={20}
            />
          </a>
        </Container>

        <Container
          className={cn(
            'network-item h-12 w-12 cursor-pointer rounded-lg bg-[#25C34A12] p-3.5',
          )}
          data-cy={DATA_CY_WHATSAPP_ICON}
        >
          <a
            href={generateShareUrl(SOCIAL_MEDIA_TYPE.WHATSAPP)}
            target='_blank'
            rel='noreferrer'
          >
            <XImage
              src={imagesList.icons.whatsappIcon}
              alt='WhatsApp'
              width={20}
              height={20}
            />
          </a>
        </Container>
        <Container
          className={cn(
            'network-item h-12 w-12 cursor-pointer rounded-lg bg-[#78767614] p-3.5',
          )}
          data-cy={DATA_CY_TWITTER_ICON}
        >
          <a
            href={generateShareUrl(SOCIAL_MEDIA_TYPE.TWITTER)}
            target='_blank'
            rel='noreferrer'
          >
            <XImage src={imagesList.icons.twitterIcon} alt='Twitter' width={20} height={20} />
          </a>
        </Container>
        <Container
          className={cn(
            'network-item h-12 w-12 cursor-pointer rounded-lg bg-[#62BFF31A] p-3.5',
          )}
          data-cy={DATA_CY_TELEGRAM_ICON}
        >
          <a
            href={generateShareUrl(SOCIAL_MEDIA_TYPE.TELEGRAM)}
            target='_blank'
            rel='noreferrer'
          >
            <XImage
              src={imagesList.icons.telegramIcon}
              alt='Telegram'
              width={20}
              height={20}
            />
          </a>
        </Container>
      </Container>
      <Container
        className={cn(
          'share-button relative z-10 h-12 w-12 cursor-pointer rounded-lg bg-[#F9FAFC] p-3.5 transition duration-300 hover:bg-[#E5E8EB]',
          expandShareIcons && 'open',
        )}
        onClick={handleShareClick}
        data-cy={DATA_CY_SHARE_BTN}
      >
        <XImage
          src={imagesList.icons.shareIcon}
          alt={productLocalization.share}
          width={20}
          height={20}
          className={cn(expandShareIcons ? 'hidden' : 'block')}
        />
      </Container>
    </Container>
  );
}
